import i18n from 'i18n';
import _ from 'lodash';
import { getByteLength } from './StringUtil';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';

export const URL_REGEX = /(https?:\/\/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/\S*)?(?:\?\S*)?(?:#\S*)?)/;

export function validateEmpty (value) {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && _.isEmpty(value)) ||
    (Array.isArray(value) && _.isEmpty(value))
  ) {
    return i18n.t<string>('formValidate.labels.emptyError');
  }
}

export function validateValidWords (
  value: string,
  customErrorMessage?: string
) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }
  // Check for emoji or special symbol using regex
  const emojiRE = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/u;
  const containsDisallowed = emojiRE.test(value);
  if (containsDisallowed) {
    return customErrorMessage
      ? customErrorMessage
      : i18n.t<string>('formValidate.labels.validWordsError');
  }
}

export function validateStringMaxLength (
  value: string,
  maxLength: number,
  customErrorMessage?: string
) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }
  const pureValue = value.replace(/[\r\n|\n|\r]+/g, '');
  const stringLength = getByteLength(pureValue);
  if (stringLength > maxLength) {
    return customErrorMessage
      ? customErrorMessage
      : i18n.t<string>('formValidate.labels.remainingCharacters', {
        num: maxLength - stringLength
      });
  }
}

export function validateMinimum (
  value,
  minValue,
  i18nKey = 'formValidate.labels.minimumError',
  currency = ''
) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (valueNumber < minValue) {
    let min = currency + minValue;
    return i18n.t<string>(i18nKey, { min });
  }
}

export function validateMinMax (value, min, max) {
  let error = validateMinimum(value, min);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (valueNumber > max) {
    return i18n.t<string>('formValidate.labels.maximumError', { max });
  }
}

export function validateMax (value, max, currency?: string, i18nKey = 'formValidate.labels.maximumError') {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (valueNumber > max) {
    return i18n.t<string>(i18nKey, { max: currency ? formatPriceWithCurrency(currency, max) : max });
  }
}

export function validateUrl (url) {
  let error = validateEmpty(url);
  if (error) {
    return error;
  }

  const urlExpression = `^${URL_REGEX.source}`;
  const regex = new RegExp(urlExpression);
  if (!url.match(regex)) {
    return i18n.t<string>('formValidate.labels.needUrl');
  }
}

export function validateEmail (email: string): string | undefined {
  if (_.isEmpty(email)) {
    return i18n.t<string>('formValidate.labels.emailRequired');
  }
  if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
    return i18n.t<string>('formValidate.labels.invalidEmail');
  }
  return undefined;
}

export function validateInteger (value) {
  let error = validateEmpty(value);
  if (error) {
    return error;
  }

  const valueNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isInteger(valueNumber)) {
    return i18n.t<string>('formValidate.labels.integerError');
  }
}
